import { RowSelectionStore } from "@/components/data-table/context/RowSelectionContext";
import { useToast } from "@/components/ui/use-toast";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { EmailRecipientsService } from "@/services/emailRecipients";
import { QueryKey, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

export const useEmailRecipientsAddUsersMutation = ({
  store,
  emailTypeId,
  onSuccess,
  queryKey,
}: {
  store: RowSelectionStore;
  emailTypeId: string;
  onSuccess: () => void;
  queryKey: QueryKey;
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutationWithErrorHandling({
    mutationFn: (checkedUserIds: string[]) =>
      EmailRecipientsService.addUsers(emailTypeId, checkedUserIds),
    errorTitle: t("emailRecipients.errors.add"),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey,
      });
      store.clear();
      onSuccess();
      toast({
        title: t("emailRecipients.success.add"),
      });
    },
  });

  const handleSubmit = useCallback(async () => {
    const checkedUserIds = Array.from(store.getSelection());

    if (checkedUserIds.length === 0) return;

    await mutation.mutateAsync(checkedUserIds);
  }, [mutation, store]);

  return {
    handleSubmit,
    isSubmitting: mutation.isPending,
  };
};
